import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AvatarUpload } from "@/components/AvatarUpload";
import { Loader2 } from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  company: string | null;
  position: string | null;
  avatar_url: string | null;
}

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin } = useUserRole();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const targetUserId = userId || user.id;
      const isOwn = targetUserId === user.id;
      setIsOwnProfile(isOwn);

      // If trying to view another user's profile, check if admin
      if (!isOwn && !isAdmin) {
        toast({
          title: "Acesso negado",
          description: "Você não tem permissão para ver este perfil",
          variant: "destructive",
        });
        navigate('/profile');
        return;
      }

      const { data: profileData, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', targetUserId)
        .single();

      if (error) throw error;

      // Fetch email from auth.users
      const { data: { user: authUser } } = await supabase.auth.admin.getUserById(targetUserId);
      
      setProfile({
        ...profileData,
        email: authUser?.email || 'Unknown'
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar perfil",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile || !isOwnProfile) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: profile.full_name,
          company: profile.company,
          position: profile.position,
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Perfil atualizado com sucesso!",
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar perfil",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (url: string) => {
    if (!profile) return;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ avatar_url: url })
        .eq('id', profile.id);

      if (error) throw error;

      setProfile({ ...profile, avatar_url: url });
      toast({
        title: "Sucesso",
        description: "Avatar atualizado com sucesso!",
      });
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar avatar",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AppSidebar onClearData={() => {}} />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </main>
        </div>
      </SidebarProvider>
    );
  }

  if (!profile) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AppSidebar onClearData={() => {}} />
          <main className="flex-1 flex items-center justify-center">
            <p>Perfil não encontrado</p>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  const canEdit = isOwnProfile;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto">
            <Card>
        <CardHeader>
          <CardTitle>
            {canEdit ? 'Meu Perfil' : 'Perfil do Usuário'}
          </CardTitle>
          <CardDescription>
            {canEdit 
              ? 'Gerencie suas informações pessoais' 
              : 'Visualizando perfil (somente leitura)'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {canEdit && (
            <AvatarUpload
              currentAvatarUrl={profile.avatar_url}
              userId={profile.id}
              userName={profile.full_name || profile.email}
              onUploadComplete={handleAvatarUpload}
            />
          )}

          {!canEdit && profile.avatar_url && (
            <div className="flex justify-center">
              <img 
                src={profile.avatar_url} 
                alt={profile.full_name || 'Avatar'} 
                className="h-32 w-32 rounded-full object-cover"
              />
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                value={profile.email}
                disabled
                className="bg-muted"
              />
            </div>

            <div>
              <Label htmlFor="full_name">Nome Completo</Label>
              <Input
                id="full_name"
                value={profile.full_name || ''}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                disabled={!canEdit}
              />
            </div>

            <div>
              <Label htmlFor="company">Empresa</Label>
              <Input
                id="company"
                value={profile.company || ''}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                disabled={!canEdit}
              />
            </div>

            <div>
              <Label htmlFor="position">Função</Label>
              <Input
                id="position"
                value={profile.position || ''}
                onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                disabled={!canEdit}
              />
            </div>

            {canEdit && (
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Profile;
